import * as Sentry from '@sentry/nextjs'
import { Extras } from '@sentry/types'
import VError from 'verror'

type TCaptureOptions = {
  /**
   * error OR message to report to Sentry
   */
  error: Error | VError | string
  severityLevel?: Sentry.SeverityLevel
  tag?: Parameters<typeof Sentry.setTag>
  extras?: Extras
}

/**
 * Sends error or message to Sentry.
 * If `error` is an instance of an `Error`, an error is reported.
 * If `error` is of type `string`, a message is sent.
 * It is possible to enrich the event with scope parameters such as tag, extras or severity level.
 *
 * Returns the event id.
 */
export const captureWithSentry = ({ severityLevel, tag, extras, error }: TCaptureOptions) => {
  // if there are no scope parameters, we will send just the error or message
  if (!severityLevel && !tag && !extras) {
    const eventId =
      typeof error === 'string'
        ? Sentry.captureMessage(error) //
        : Sentry.captureException(error)

    return eventId
  }

  // if there are scope parameters, we will send the error or message with enriched scope
  // https://docs.sentry.io/platforms/javascript/enriching-events/scopes/#local-scopes
  // push/pop scope is an alternative to the Sentry.withScope, that won’t allow us to get the eventId

  const scope = Sentry.getCurrentHub().pushScope()

  if (severityLevel) scope.setLevel(severityLevel)
  if (tag) scope.setTag(...tag)
  if (extras) scope.setExtras(extras)

  const eventId =
    error instanceof Error
      ? Sentry.captureException(error) //
      : Sentry.captureMessage(error)

  Sentry.getCurrentHub().popScope()

  return eventId
}

/**
 * helper function that creates a typed data structure  for storing individual extras items
 */
export const createExtrasMap = () => new Map<string, unknown>()

/**
 * helper function that converts entries to the Extras object used by scope.setExtras()
 */
export const createExtras = (extrasMap: ReturnType<typeof createExtrasMap>) =>
  Object.fromEntries(extrasMap)
